import React, { useState } from 'react';
import { X, Database, Users, CreditCard, FileText, Calendar, Receipt, TrendingUp } from 'lucide-react';
import ERDiagram from './ERDiagram';
import DatasetSchemaModal from './DataSetSchemaModal';

const dataModelType = {
  INVOICE_TRANSACTION: 'invoice_transaction',
  HIERARCHICAL: 'hierarchical',
  PLAN_SUBSCRIPTION: 'plan_subscription'
};

interface SchemaDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSchemaModal: () => void;
}

const SchemaDrawer: React.FC<SchemaDrawerProps> = ({ isOpen, onClose, onOpenSchemaModal }) => {
  const [activeTab, setActiveTab] = useState<string>(dataModelType.INVOICE_TRANSACTION);
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!isOpen) return null;

  // Invoice + Transaction Schema (based on existing API data)
  const invoiceTransactionSchema = {
    entities: [
      {
        id: 'transactions',
        name: 'transactions',
        icon: CreditCard,
        color: 'bg-emerald-50 border-emerald-200',
        iconColor: 'text-emerald-600',
        x: 50,
        y: 50,
        fields: [
          { name: 'transaction_id', type: 'VARCHAR(50)', isPrimary: true, required: true },
          { name: 'customer_id', type: 'VARCHAR(50)', isForeign: true, references: 'CUSTOMER.id', required: true },
          { name: 'group_id', type: 'VARCHAR(50)', required: true },
          { name: 'customer_name', type: 'VARCHAR(255)', required: true },
          { name: 'transaction_amount', type: 'Float', required: true },
          { name: 'transaction_type', type: 'ENUM', required: true },
          { name: 'transaction_currency', type: 'VARCHAR(3)', required: true },
          { name: 'transaction_status', type: 'VARCHAR(50)', required: true },
          { name: 'line_item_type', type: 'ENUM', required: true },
          { name: 'date', type: 'TIMESTAMP', required: true },
          { name: 'invoice_id', type: 'VARCHAR(50)', isForeign: true, references: 'INVOICES.id', required: false }
        ]
      },
      {
        id: 'invoices',
        name: 'invoices',
        icon: FileText,
        color: 'bg-indigo-50 border-indigo-200',
        iconColor: 'text-indigo-600',
        x: 450,
        y: 50,
        fields: [
          { name: 'id', type: 'VARCHAR(50)', isPrimary: true, required: true },
          { name: 'customer_id', type: 'VARCHAR(50)', isForeign: true, references: 'CUSTOMER.id', required: true },
          { name: 'group_id', type: 'VARCHAR(50)', required: true },
          { name: 'issue_date', type: 'DATE', required: true },
          { name: 'invoice_amount', type: 'Float', required: true },
          { name: 'currency', type: 'VARCHAR(3)', required: true },
          { name: 'due_date', type: 'DATE', required: false }
        ]
      },
      {
        id: 'invoice_payments',
        name: 'invoice_payments',
        icon: Receipt,
        color: 'bg-pink-50 border-pink-200',
        iconColor: 'text-pink-600',
        x: 50,
        y: 350,
        fields: [
          { name: 'id', type: 'VARCHAR(50)', isPrimary: true, required: true },
          { name: 'invoice_id', type: 'VARCHAR(50)', isForeign: true, references: 'invoices.id', required: true },
          { name: 'amount', type: 'Float', required: true },
          { name: 'status', type: 'VARCHAR(50)', required: true },
          { name: 'created_at', type: 'TIMESTAMP', required: true }
        ]
      }
    ],
    relationships: [
      { from: 'invoices', to: 'transactions', label: 'paid_by', cardinality: '1:many' },
      { from: 'invoices', to: 'invoice_payments', label: 'has', cardinality: '1:many' }
    ]
  };

  // Plan + Subscription Schema
  const planSubscriptionSchema = {
    entities: [
      {
        id: 'plans',
        name: 'plans',
        icon: Calendar,
        color: 'bg-blue-50 border-blue-200',
        iconColor: 'text-blue-600',
        x: 50,
        y: 50,
        fields: [
          { name: 'plan_id', type: 'VARCHAR(50)', isPrimary: true, required: true },
          { name: 'plan_name', type: 'VARCHAR(255)', required: true },
          { name: 'billing_scheme', type: 'VARCHAR(50)', required: true },
          { name: 'amount', type: 'Float', required: true },
          { name: 'currency', type: 'VARCHAR(3)', required: true },
          { name: 'interval_of_billing', type: 'VARCHAR(20)', required: true },
          { name: 'interval_count', type: 'INT', required: true },
          { name: 'trial_period_days', type: 'INT', required: false }
        ]
      },
      {
        id: 'subscriptions',
        name: 'subscriptions',
        icon: Database,
        color: 'bg-purple-50 border-purple-200',
        iconColor: 'text-purple-600',
        x: 400,
        y: 50,
        fields: [
          { name: 'id', type: 'VARCHAR(50)', isPrimary: true, required: true },
          { name: 'customer_id', type: 'VARCHAR(50)', isForeign: true, references: 'CUSTOMER.id', required: true },
          { name: 'plan_id', type: 'VARCHAR(50)', isForeign: true, references: 'PLANS.plan_id', required: true },
          { name: 'billing_type', type: 'VARCHAR(50)', required: true },
          { name: 'status', type: 'VARCHAR(50)', required: true },
          { name: 'currency', type: 'VARCHAR(3)', required: true },
          { name: 'start_date', type: 'TIMESTAMP', required: true },
          { name: 'end_date', type: 'TIMESTAMP', required: false },
          { name: 'trial_start_date', type: 'TIMESTAMP', required: false },
          { name: 'trial_end_date', type: 'TIMESTAMP', required: false }
        ]
      }
    ],
    relationships: [
      { from: 'plans', to: 'subscriptions', label: 'defines', cardinality: '1:many' }
    ]
  };

  // Complete Revenue Data Schema (Hierarchical)
  const hierarchicalSchema = {
    entities: [
      {
        id: 'plan',
        name: 'plan',
        icon: Calendar,
        color: 'bg-blue-50 border-blue-200',
        iconColor: 'text-blue-600',
        x: 50,
        y: 50,
        fields: [
          { name: 'id', type: 'int', isPrimary: true, required: true },
          { name: 'name', type: 'string', required: true },
          { name: 'interval_count', type: 'int', required: true },
          { name: 'interval_unit', type: 'string', required: true },
          { name: 'plan_id', type: 'string', required: true },
          { name: 'created_at', type: 'timestamp', required: true },
          { name: 'currency', type: 'VARCHAR(3)', required: false },
          { name: 'trial_period_days', type: 'int', required: false },
          { name: 'amount', type: 'bigint', required: false }

        ]
      },
      {
        id: 'customer',
        name: 'customer',
        icon: Users,
        color: 'bg-green-50 border-green-200',
        iconColor: 'text-green-600',
        x: 50,
        y: 300,
        fields: [
          { name: 'id', type: 'int', isPrimary: true, required: true }, 
          { name: 'name', type: 'string', required: true },
          { name: 'email', type: 'string', required: false },
          { name: 'company', type: 'string', required: true },
          { name: 'country', type: 'string', required: true },
          { name: 'state', type: 'string', required: false },
          { name: 'city', type: 'string', required: false },
          { name: 'zip', type: 'string', required: false },
          { name: 'created_at', type: 'timestamp', required: false },
          { name: 'trail_start_date', type: 'timestamp', required: false },
          { name: 'balance', type: 'bigint', required: false },
          { name: 'currency', type: 'bigint', required: false },
          { name: 'delinquent', type: 'boolean', required: true }
        ]
      },
      {
        id: 'subscription',
        name: 'subscription',
        icon: Database,
        color: 'bg-purple-50 border-purple-200',
        iconColor: 'text-purple-600',
        x: 400,
        y: 175,
        fields: [
          { name: 'id', type: 'int', isPrimary: true, required: true }, 
          { name: 'customer_id', type: 'int', isForeign: true, references: 'CUSTOMER.id', required: true },
          { name: 'plan_id', type: 'int', isForeign: true, references: 'PLAN.id', required: true },
          { name: 'event_type', type: 'string', required: true },
          { name: 'currency', type: 'VARCHAR(3)', required: true },
          { name: 'amount', type: 'bigint', required: true },
          { name: 'quantity', type: 'int', required: true },
          { name: 'start_at', type: 'timestamp', required: true },
          { name: 'status', type: 'string', required: true },     
          { name: 'current_term_end', type: 'timestamp', required: true },
          { name: 'current_term_start', type: 'timestamp', required: true },
          { name: 'trial_start', type: 'timestamp', required: false },
          { name: 'trial_end', type: 'timestamp', required: false },
          { name: 'latest_invoice_id', type: 'VARCHAR(50)', required: false },
          
        ]
      },
      {
        id: 'subscription_events',
        name: 'subscription_events',
        icon: TrendingUp,
        color: 'bg-orange-50 border-orange-200',
        iconColor: 'text-orange-600',
        x: 700,
        y: 100,
        fields: [
          { name: 'event_id', type: 'int', isPrimary: true, required: true },
          { name: 'subscription_id', type: 'int', isForeign: true, references: 'SUBSCRIPTION.id', required: true },
          { name: 'customer_id', type: 'int', isForeign: true, references: 'CUSTOMER.id', required: true },
          { name: 'event_type', type: 'string', required: true },
          { name: 'subscription_status', type: 'string', required: true },
          { name: 'date', type: 'timestamp', required: true },
          { name: 'effective_date', type: 'timestamp', required: true },
          { name: 'plan_id', type: 'int', isForeign: true, references: 'PLAN.id', required: true },
          { name: 'currency', type: 'VARCHAR(3)', required: true },
          { name: 'amount', type: 'bigint', required: true },
          { name: 'quantity', type: 'int', required: true }
        ]
      },
      {
        id: 'invoice_line_items',
        name: 'invoice_line_items',
        icon: Receipt,
        color: 'bg-pink-50 border-pink-200',
        iconColor: 'text-pink-600',
        x: 50,
        y: 650,
        fields: [
          { name: 'id', type: 'int', isPrimary: true, required: true },
          { name: 'invoice_id', type: 'int', isForeign: true, references: 'INVOICES.id', required: true },
          { name: 'subscription_id', type: 'int', isForeign: true, references: 'SUBSCRIPTION.id', required: true },
          { name: 'type', type: 'string', required: true },
          { name: 'amount', type: 'bigint', required: true },
          { name: 'plan_id', type: 'int', isForeign: true, references: 'PLAN.id', required: true },
          { name: 'period_start', type: 'timestamp', required: true },
          { name: 'period_end', type: 'timestamp', required: true },
          { name: 'quantity', type: 'int', required: true },
          { name: 'proration', type: 'boolean', required: true },
          { name: 'proration_type', type: 'string', required: false },
          { name: 'discount_amount', type: 'bigint', required: false },
          { name: 'tax_amount', type: 'bigint', required: false },
          { name: 'description', type: 'string', required: false },
          { name: 'transaction_fees', type: 'bigint', required: false },
          { name: 'transaction_fees_currency', type: 'VARCHAR(3)', required: false },
          { name: 'balance_transfer', type: 'boolean', required: false },
          { name: 'currency', type: 'VARCHAr(3)', required: false }          
        ]
      },
      {
        id: 'invoices',
        name: 'invoices',
        icon: FileText,
        color: 'bg-indigo-50 border-indigo-200',
        iconColor: 'text-indigo-600',
        x: 400,
        y: 600,
        fields: [
          { name: 'id', type: 'int', isPrimary: true, required: true },
          { name: 'customer_id', type: 'int', isForeign: true, references: 'CUSTOMER.id', required: true },
          { name: 'amount', type: 'bigint', required: true },
          { name: 'issue_date', type: 'date', required: true },
          { name: 'due_date', type: 'date', required: true },
          { name: 'currency', type: 'VARCHAR(3)', required: true },
          { name: 'status', type: 'VARCHAR(10)', required: true },
        ]
      },
      {
        id: 'transactions',
        name: 'transactions',
        icon: CreditCard,
        color: 'bg-emerald-50 border-emerald-200',
        iconColor: 'text-emerald-600',
        x: 700,
        y: 500,
        fields: [
          { name: 'id', type: 'int', isPrimary: true, required: true },
          { name: 'invoice_id', type: 'int', isForeign: true, references: 'INVOICES.id', required: true },
          { name: 'type', type: 'string', required: true },
          { name: 'result', type: 'string', required: true },
          { name: 'date', type: 'timestamp', required: true }
        ]
      }
    ],
    relationships: [
      { from: 'plan', to: 'subscription', label: 'defines', cardinality: '1:many' },
      { from: 'customer', to: 'subscription', label: 'has', cardinality: '1:many' },
      { from: 'customer', to: 'invoices', label: 'receives', cardinality: '1:many' },
      { from: 'subscription', to: 'subscription_events', label: 'generates', cardinality: '1:many' },
      { from: 'subscription_events', to: 'plan', label: 'references', cardinality: 'many:1' },
      { from: 'subscription', to: 'invoice_line_items', label: 'bills_for', cardinality: '1:many' },
      { from: 'invoices', to: 'invoice_line_items', label: 'contains', cardinality: '1:many' },
      { from: 'invoices', to: 'transactions', label: 'paid_by', cardinality: '1:many' }
    ]
  };

  const tabs = [
    {
      id: dataModelType.INVOICE_TRANSACTION,
      label: 'Transactions + Invoices',
      description: 'Transaction and invoices data',
      schema: invoiceTransactionSchema
    },
    {
      id: dataModelType.HIERARCHICAL,
      label: 'Complete Revenue Data',
      description: 'Complete entity relationships',
      schema: hierarchicalSchema
    },
    {
      id: dataModelType.PLAN_SUBSCRIPTION,
      label: 'Plan + Subscription',
      description: 'Subscription plans and customer subscriptions',
      schema: planSubscriptionSchema
    }
  ];

  const activeTabData = tabs.find(tab => tab.id === activeTab);

  const handleFullscreen = () => {
    setIsFullscreen(true);
  };

  const handleExitFullscreen = () => {
    setIsFullscreen(false);
  };

  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-20 top-9 right-0 bg-white flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Database className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ms-4">
              <h2 className="ms-4 text-xl font-semibold text-gray-900">{activeTabData?.label} - ER Diagram</h2>
              <p className="ms-4 text-sm text-gray-600">{activeTabData?.description}</p>
            </div>
          </div>
          <button
            onClick={handleExitFullscreen}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-8 scrollbar-hide">
          <div className="flex justify-center items-start min-h-full">
            <div className="w-full max-w-6xl">
              {activeTabData && (
                <ERDiagram
                  entities={activeTabData.schema.entities}
                  relationships={activeTabData.schema.relationships}
                  isFullscreen={true}
                  onExitFullscreen={handleExitFullscreen}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-20 overflow-hidden mt-6">
      <div className="absolute inset-0 bg-black bg-opacity-50 mt-6" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full max-w-7xl bg-white shadow-xl mt-6">
        {/* Header */}
        <div className="flex items-center p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg me-4">
              <Database className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Entity Relationship Diagram</h2>
              <p className="text-sm text-gray-600">Choose your data structure and view the ER diagram</p>
            </div>
          </div>
          <button className='ml-auto text-blue-600 hover:text-blue-800 underline hover:underline-offset-2 bg-transparent px-0 py-0 text-sm font-medium transition-colors flex items-center gap-1'
              onClick={onOpenSchemaModal}
            >
              Dev Guide: Data Model Schema
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {activeTabData && (
            <div className="h-full min-h-[800px]">
              {/* Tab Description */}
              <div className="p-4 pl-7 bg-blue-50 border-b border-blue-200">
                <h3 className="font-medium text-blue-900 mb-1">{activeTabData.label}</h3>
                <p className="text-sm text-blue-800">{activeTabData.description}</p>
              </div>

              {/* ER Diagram */}
              <div className="h-[calc(100vh-200px)] min-h-[600px] p-4 ms-5">
                <ERDiagram
                  entities={activeTabData.schema.entities}
                  relationships={activeTabData.schema.relationships}
                  isFullscreen={false}
                  onFullscreen={handleFullscreen}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchemaDrawer;
